/**
 * Props contract for the ScenarioExplainerClip social template.
 *
 * Everything on screen is driven by these validated props — no AI-written or
 * executable scene code. Numbers come from the Praevue engine; copy (hook,
 * beats, CTA) is plain text. Invalid or missing required data throws, which
 * fails the render instead of producing placeholder frames.
 */
import { z } from "zod";

export const finite = z.number().finite();

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
export const hexColor = z.string().trim().regex(HEX_COLOR, "must be a hex color like #1D4ED8");

export const httpUrl = z
  .string()
  .trim()
  .max(2048)
  .refine((value) => /^https?:\/\/[^\s]+$/i.test(value), "must be an http(s) URL");

/** Dollar amount or the literal "Unlimited" (case-insensitive). */
export const amountOrUnlimited = z.union([
  finite,
  z
    .string()
    .trim()
    .refine((value) => value.toLowerCase() === "unlimited", 'must be a number or "Unlimited"')
    .transform(() => "Unlimited"),
]);

export const payoffPoint = z.object({
  underlyingPrice: finite.positive(),
  pnl: finite,
});

/** At least 2 points, sorted by underlying price (input order does not matter). */
export const payoffSeries = z
  .array(payoffPoint)
  .min(2, "needs at least 2 points")
  .max(400)
  .transform((points) => [...points].sort((a, b) => a.underlyingPrice - b.underlyingPrice));

export const EMPHASIS_FORMATS = [
  "currency",
  "signedCurrency",
  "price",
  "percent",
  "signedPercent",
  "number",
  "signedNumber",
];

export const emphasisSchema = z.object({
  value: finite,
  format: z.enum(EMPHASIS_FORMATS).default("currency"),
  label: z.string().trim().max(48).default(""),
});

export const beatSchema = z.object({
  text: z.string().trim().min(1, "beat text is required").max(140),
  emphasis: emphasisSchema.nullable().default(null),
  /** Underlying price the chart's price marker moves to during this beat. */
  markerPrice: finite.positive().nullable().default(null),
});

export const wordTimingSchema = z.object({
  text: z.string().trim().min(1).max(64),
  startMs: finite.nonnegative(),
  endMs: finite.nonnegative(),
});

const voiceoverSchema = z.object({
  audioUrl: httpUrl,
  /** Optional ElevenLabs-style word timings, relative to the start of the video. */
  words: z.array(wordTimingSchema).max(2000).default([]),
  /** Optional known audio length; avoids probing the audio file. */
  durationSeconds: finite.positive().max(600).nullable().default(null),
});

export const DEFAULT_BRAND = {
  name: "Praevue",
  logoUrl: null,
  colors: {
    background: "#0B1220",
    surface: "#16213A",
    text: "#FFFFFF",
    muted: "#A5B4CC",
    primary: "#1D4ED8",
    accent: "#93C5FD",
    negative: "#F87171",
  },
};

export const brandSchema = z
  .object({
    name: z.string().trim().min(1).max(40).default(DEFAULT_BRAND.name),
    logoUrl: httpUrl.nullable().default(null),
    colors: z
      .object({
        background: hexColor.default(DEFAULT_BRAND.colors.background),
        surface: hexColor.default(DEFAULT_BRAND.colors.surface),
        text: hexColor.default(DEFAULT_BRAND.colors.text),
        muted: hexColor.default(DEFAULT_BRAND.colors.muted),
        primary: hexColor.default(DEFAULT_BRAND.colors.primary),
        accent: hexColor.default(DEFAULT_BRAND.colors.accent),
        negative: hexColor.default(DEFAULT_BRAND.colors.negative),
      })
      .prefault({}),
  })
  .prefault({});

export const scenarioExplainerSchema = z
  .object({
    ticker: z
      .string()
      .trim()
      .min(1, "ticker is required")
      .max(12)
      .transform((value) => value.toUpperCase()),
    strategyLabel: z.string().trim().min(1, "strategyLabel is required").max(60),
    legsSummary: z.string().trim().min(1, "legsSummary is required").max(160),
    spot: finite.positive(),
    numbers: z.object({
      /** Worst-case P&L in dollars; sign is ignored (always shown as a loss). */
      maxLoss: amountOrUnlimited,
      /** Best-case P&L in dollars, or "Unlimited". */
      maxGain: amountOrUnlimited,
      breakevens: z.array(finite.positive()).min(1, "at least one breakeven is required").max(4),
      /** > 0 = net debit paid, < 0 = net credit received (dollars). */
      netDebitCredit: finite,
    }),
    payoff: z.object({
      expiration: payoffSeries,
      tPlus0: payoffSeries.nullable().default(null),
    }),
    hook: z.string().trim().min(1, "hook is required").max(120),
    beats: z.array(beatSchema).min(3, "needs 3-5 beats").max(5, "needs 3-5 beats"),
    cta: z
      .object({
        text: z.string().trim().min(1).max(60).default("Try it yourself"),
        url: z
          .string()
          .trim()
          .min(1, "cta.url is required")
          .max(80)
          .transform((value) => value.replace(/^https?:\/\//i, "").replace(/\/+$/, "")),
      }),
    dataAsOf: z.string().trim().min(1, "dataAsOf is required").max(48),
    disclaimer: z.string().trim().min(1, "disclaimer text is required").max(320),
    voiceover: voiceoverSchema.nullable().default(null),
    brand: brandSchema,
    fps: finite.positive().nullable().default(null),
  })
  .superRefine((props, ctx) => {
    const prices = props.payoff.expiration.map((p) => p.underlyingPrice);
    const lo = Math.min(...prices);
    const hi = Math.max(...prices);
    if (!(hi > lo)) {
      ctx.addIssue({
        code: "custom",
        path: ["payoff", "expiration"],
        message: "underlyingPrice values must span a range",
      });
    }
    props.voiceover?.words?.forEach((word, index) => {
      if (word.endMs < word.startMs) {
        ctx.addIssue({
          code: "custom",
          path: ["voiceover", "words", index, "endMs"],
          message: "endMs must be >= startMs",
        });
      }
    });
  });

function formatIssues(error) {
  return error.issues
    .map((issue) => `${issue.path.length ? issue.path.join(".") : "(root)"}: ${issue.message}`)
    .join("; ");
}

/**
 * Validate + normalise props. Throws (fails the render) on any invalid or
 * missing required field. Idempotent: parsing already-parsed props is safe.
 */
export function parseScenarioExplainerProps(input) {
  const result = scenarioExplainerSchema.safeParse(input ?? {});
  if (!result.success) {
    throw new Error(`ScenarioExplainerClip: invalid props — ${formatIssues(result.error)}`);
  }
  return result.data;
}

/**
 * Remotion shallow-merges inputProps over a composition's defaultProps. The clip
 * therefore lives under a single `clip` key: a render payload replaces the
 * Studio fixture wholesale and can never inherit demo numbers field-by-field.
 */
export function parseClipInputProps(inputProps) {
  // Clip fields sent flat would be silently ignored in favour of the Studio fixture.
  const misplaced = Object.keys(scenarioExplainerSchema.shape ?? {}).filter(
    (key) => inputProps && Object.prototype.hasOwnProperty.call(inputProps, key)
  );
  if (misplaced.length) {
    throw new Error(
      `ScenarioExplainerClip: invalid props — ${misplaced.join(", ")}: must be nested under inputProps.clip`
    );
  }
  const clip = inputProps?.clip;
  if (!clip || typeof clip !== "object" || Array.isArray(clip)) {
    throw new Error("ScenarioExplainerClip: invalid props — clip: inputProps.clip object is required");
  }
  return parseScenarioExplainerProps(clip);
}
